import DOMPurify from 'dompurify'

import {
  type ChangelogReleaseConstructor,
  type ChangelogTerm,
} from '@webapp/platform/discover-more/entities/ChangelogRelease'
import {
  type ChangelogReleaseApiModel,
  type ChangelogMediaApiModel,
} from '../api-models/ChangelogReleaseApiModel'

// WordPress excerpts append a trailing "more-link" anchor (with a
// screen-reader-text span) that isn't part of the visible copy.
const MORE_LINK_PATTERN =
  /<a[^>]*class="[^"]*more-link[^"]*"[^>]*>[\s\S]*?<\/a>/gi

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'ul',
    'ol',
    'li',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'code',
    'a',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
}

/**
 * `title` is untrusted HTML from an external CMS; it renders inside heading
 * typography, so reduce it to plain text (this also decodes HTML entities).
 */
const stripChangelogHtmlToText = (html: string): string => {
  const withoutMoreLink = html.replace(MORE_LINK_PATTERN, '')

  const doc = new DOMParser().parseFromString(withoutMoreLink, 'text/html')

  return (doc.body.textContent ?? '').trim()
}

/**
 * `excerpt` and `content` are untrusted HTML from an external CMS. Preserve the
 * formatting but run it through an allowlist so the sanitized markup is safe to
 * hand to `dangerouslySetInnerHTML`. Links are hardened to open in a new tab.
 */
const sanitizeChangelogHtml = (html: string): string => {
  const withoutMoreLink = html.replace(MORE_LINK_PATTERN, '')

  const clean = DOMPurify.sanitize(withoutMoreLink, SANITIZE_CONFIG)

  const doc = new DOMParser().parseFromString(clean, 'text/html')

  doc.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
  })

  return doc.body.innerHTML.trim()
}

const VISIBILITY_TAXONOMY = 'visibility'
const CHANGE_TYPE_TAXONOMY = 'change_type'
const PRODUCT_CATEGORY_TAXONOMY = 'product_category'
const FEATURED_TERM_SLUG = 'featured'

// Widest registered size first — the promo surfaces render the image at up to
// ~2x its card width. `source_url` (the original upload) is the last resort.
const PREFERRED_IMAGE_SIZES = ['medium_large', 'large', 'full']

const getFeaturedMedia = (
  apiModel: ChangelogReleaseApiModel
): ChangelogMediaApiModel | undefined =>
  apiModel._embedded?.['wp:featuredmedia']?.[0]

const getImageUrl = (apiModel: ChangelogReleaseApiModel): Nullable<string> => {
  const media = getFeaturedMedia(apiModel)
  const sizes = media?.media_details?.sizes ?? {}

  const preferred = PREFERRED_IMAGE_SIZES.map(
    (size) => sizes[size]?.source_url
  ).find(Boolean)

  return preferred ?? media?.source_url ?? null
}

const getIsFeatured = (apiModel: ChangelogReleaseApiModel): boolean =>
  (apiModel._embedded?.['wp:term'] ?? []).some((group) =>
    group.some(
      (term) =>
        term.taxonomy === VISIBILITY_TAXONOMY &&
        term.slug === FEATURED_TERM_SLUG
    )
  )

const getTerm = (
  apiModel: ChangelogReleaseApiModel,
  taxonomy: string
): Nullable<ChangelogTerm> => {
  for (const group of apiModel._embedded?.['wp:term'] ?? []) {
    const match = group.find((term) => term.taxonomy === taxonomy)

    if (match?.slug && match.name) {
      return { slug: match.slug, name: match.name }
    }
  }

  return null
}

export const mapChangelogReleaseApiModel = (
  apiModel: ChangelogReleaseApiModel
): ChangelogReleaseConstructor => ({
  id: apiModel.id,
  title: stripChangelogHtmlToText(apiModel.title.rendered),
  excerpt: sanitizeChangelogHtml(apiModel.excerpt.rendered),
  body: apiModel.content?.rendered
    ? sanitizeChangelogHtml(apiModel.content.rendered)
    : sanitizeChangelogHtml(apiModel.excerpt.rendered),
  url: apiModel.link,
  publishedAt: apiModel.date,
  imageUrl: getImageUrl(apiModel),
  imageAlt: getFeaturedMedia(apiModel)?.alt_text ?? null,
  isFeatured: getIsFeatured(apiModel),
  changeType: getTerm(apiModel, CHANGE_TYPE_TAXONOMY),
  productCategory: getTerm(apiModel, PRODUCT_CATEGORY_TAXONOMY),
})
